import React, { useState, useRef, useCallback } from "react";
import { Phone, CheckCircle2, AlertTriangle, Bot } from "lucide-react";

// Asterisk / FreeSWITCH / Kamailio snippet dictionaries
const SNIPPETS = {
  asterisk: {
    ext: "exten => §1§,1,§2§",
    extn: "exten => §1§,n,§2§",
    dial: "Dial(SIP/§1§)",
    play: "Playback(§1§)",
    ans: "Answer()",
    hang: "Hangup()",
    vm: "VoiceMail(§1§)",
    vmm: "VoiceMailMain()",
    set: "Set(§1§=§2§)",
    goto: "Goto(§1§,§2§,§3§)",
    gotoif: 'GotoIf($[§1§]?§2§:§3§)',
    record: "Record(§1§)",
    wait: "Wait(§1§)",
    nrole: "NoOp(§1§)",
    bg: "Background(§1§)",
    ctx: "[§1§]",
  },
  freeswitch: {
    ext: '<extension name="§1§">\n  <condition field="destination_number" expression="^§1§$">\n    §2§\n  </condition>\n</extension>',
    ans: '<action application="answer"/>',
    echo: '<action application="echo"/>',
    bridge: '<action application="bridge" data="§1§"/>',
    play: '<action application="playback" data="§1§"/>',
    hang: '<action application="hangup"/>',
    set: '<action application="set" data="§1§=§2§"/>',
    log: '<action application="log" data="INFO §1§"/>',
    sleep: '<action application="sleep" data="§1§"/>',
    voicemail: '<action application="voicemail" data="default ${domain_name} §1§"/>',
    cond: '<condition field="§1§" expression="§2§">\n  §3§\n</condition>',
  },
  kamailio: {
    route: "route[§1§] {\n  §2§\n}",
    iff: "if (§1§) {\n  §2§\n}",
    fwd: "forward();",
    reply: 'sl_send_reply("200", "OK");',
    log: 'xlog("L_INFO", "§1§");',
    method: 'is_method("§1§")',
    inv: 'is_method("INVITE")',
    ureg: 'is_method("REGISTER")',
    auth: "auth_check();",
    rec: "record_route();",
    drop: "drop;",
    exit: "exit;",
    sleep: "sleep(§1§);",
  },
};

const MODE_INFO = {
  asterisk:   { label: "Asterisk",    placeholder: "[from-internal]\nexten => 1234,1,Answer()\nexten => 1234,n,Playback(welcome)\nexten => 1234,n,Hangup()" },
  freeswitch: { label: "FreeSWITCH",  placeholder: "<extension name=\"echo\">\n  <condition field=\"destination_number\" expression=\"^9196$\">\n    <action application=\"answer\"/>\n    <action application=\"echo\"/>\n  </condition>\n</extension>" },
  kamailio:   { label: "Kamailio",    placeholder: "route {\n  if (is_method(\"INVITE\")) {\n    sl_send_reply(\"200\", \"OK\");\n  }\n}" },
};

export default function DialplanEditor() {
  const [mode, setMode] = useState("asterisk");
  const [code, setCode] = useState("");
  const [validation, setValidation] = useState(null);
  const textareaRef = useRef(null);

  const handleKeyDown = useCallback((e) => {
    if (e.key !== "Tab") return;
    e.preventDefault();
    const ta = textareaRef.current;
    const start = ta.selectionStart;
    const before = code.substring(0, start);
    const idx = Math.max(before.lastIndexOf(" "), before.lastIndexOf("\n"), before.lastIndexOf("\t"));
    const word = before.substring(idx + 1);
    const snippets = SNIPPETS[mode] || {};

    if (snippets[word]) {
      // Auto-expand snippet
      const tpl = snippets[word].replace(/§\d+§/g, "___");
      const newCode = code.substring(0, idx + 1) + tpl + code.substring(start);
      setCode(newCode);
      // Position cursor at first ___
      const cursorPos = (idx + 1) + tpl.indexOf("___");
      setTimeout(() => {
        if (cursorPos >= 0) {
          ta.selectionStart = cursorPos;
          ta.selectionEnd = cursorPos + 3;
        }
        ta.focus();
      }, 0);
    } else {
      // Insert 2-space indent
      const newCode = code.substring(0, start) + "  " + code.substring(start);
      setCode(newCode);
      setTimeout(() => {
        ta.selectionStart = start + 2;
        ta.selectionEnd = start + 2;
        ta.focus();
      }, 0);
    }
  }, [code, mode]);

  const validate = () => {
    if (!code.trim()) {
      setValidation({ ok: false, msg: "Empty dialplan" });
      return;
    }
    let count = 0;
    let issues = [];
    if (mode === "asterisk") {
      count = (code.match(/^exten\s*=>/gm) || []).length;
      if (count === 0) issues.push("No extensions found");
    } else if (mode === "freeswitch") {
      count = (code.match(/<extension/g) || []).length;
      if (count === 0) issues.push("No <extension> blocks found");
      if ((code.match(/<extension/g) || []).length !== (code.match(/<\/extension>/g) || []).length) {
        issues.push("Unbalanced <extension> tags");
      }
    } else if (mode === "kamailio") {
      count = (code.match(/route\s*[\[\{]/g) || []).length;
      if (count === 0) issues.push("No route blocks found");
      if ((code.match(/{/g) || []).length !== (code.match(/}/g) || []).length) {
        issues.push("Unbalanced braces");
      }
    }
    setValidation({ ok: issues.length === 0, msg: issues.length ? issues.join("; ") : `Looks good. ${count} ${mode === "kamailio" ? "route(s)" : "extension(s)"} found.` });
    setExplaining(true); setExplanation("");
    fetch("/api/sip-test/explain-dialplan", {
      method: "POST", headers: {"Content-Type": "application/json"},
      body: JSON.stringify({content: code.trim(), mode})
    }).then(function(r){ return r.json(); })
      .then(function(d){ setExplanation(d.explanation || "No explanation"); setExplaining(false); })
      .catch(function(e){ setExplanation("Error: " + e.message); setExplaining(false); });
  };

  const info = MODE_INFO[mode];


  const [explanation, setExplanation] = React.useState("");
  const [explaining, setExplaining] = React.useState(false);
  const explainDialplan = async () => {
    const txt = typeof code === "string" ? code.trim() : "";
    if (!txt) return;
    setExplaining(true); setExplanation("");
    try {
      const res = await fetch("/api/sip-test/explain-dialplan", {
        method:"POST", headers:{"Content-Type":"application/json",
          "Authorization":"Bearer "+(sessionStorage.getItem("aegis_token")||"")},
        body: JSON.stringify({content:txt, mode})
      });
      const d = await res.json();
      setExplanation(d.explanation || "No explanation");
    } catch(e) { setExplanation("Failed: "+e.message); }
    finally { setExplaining(false); }
  };
  return (
    <div className="au-glass au-pad">
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 16, flexWrap: "wrap" }}>
        <h2 style={{ fontSize: "1.25rem", display: "flex", alignItems: "center", gap: 8, color: "var(--au-accent-ink)" }}>
          <Phone size={20} /> Dialplan Editor
        </h2>
        <select
          className="au-input"
          value={mode}
          onChange={(e) => { setMode(e.target.value); setValidation(null); }}
          style={{ width: "auto", padding: "8px 12px" }}
        >
          {Object.entries(MODE_INFO).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <span style={{ color: "var(--au-muted)", fontSize: 12 }}>
          Press <kbd style={{ background: "var(--au-glass-strong)", border: "1px solid var(--au-hair)", padding: "2px 6px", borderRadius: 4, fontFamily: "var(--au-font-mono)" }}>Tab</kbd> after keywords to auto-expand
        </span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 240px", gap: 16 }}>
        <div>
          <textarea
            ref={textareaRef}
            className="au-input au-mono"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={info.placeholder}
            style={{
              minHeight: 360, fontFamily: "var(--au-font-mono)", fontSize: 13,
              lineHeight: 1.6, resize: "vertical", caretColor: "var(--au-accent)",
            }}
            spellCheck={false}
          />
          <div style={{ display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
            <button className="au-pill au-pill-primary au-pill-sm" onClick={validate}>
              Validate
            </button>
            <button className="au-pill au-pill-glass au-pill-sm" onClick={() => { setCode(""); setValidation(null); }}>
              Clear
            </button>
            <button className="au-pill au-pill-glass au-pill-sm" onClick={() => setCode(info.placeholder)}>
              Load Example
            </button>
          </div>
          {validation && (
            <div
              style={{
                marginTop: 10,
                padding: "10px 14px",
                display: "flex",
                alignItems: "center",
                gap: 8,
                background: validation.ok ? "rgba(21,134,10,0.1)" : "rgba(211,34,8,0.1)",
                color: validation.ok ? "var(--au-success)" : "var(--au-error)",
                border: `1px solid ${validation.ok ? "var(--au-success)" : "var(--au-error)"}`,
                borderRadius: "var(--au-r-input)",
                fontSize: 13,
              }}
            >
              {validation.ok ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />} {validation.msg}
            </div>
          )}
          {explaining && (
            <div
              style={{
                marginTop: 12,
                padding: 16,
                textAlign: "center",
                color: "var(--au-accent-ink)",
                background: "var(--au-glass-strong)",
                border: "1px solid var(--au-border)",
                borderRadius: "var(--au-r-input)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}
            >
              <Bot size={16} /> Analyzing dialplan with AI... please wait
            </div>
          )}
          {!explaining && explanation && (
            <div
              style={{
                marginTop: 12,
                background: "var(--au-glass-strong)",
                border: "1px solid var(--au-border)",
                borderRadius: "var(--au-r-input)",
                padding: "16px 20px",
              }}
            >
              <div style={{ fontSize: 12, color: "var(--au-accent-ink)", fontWeight: 700, letterSpacing: 2, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
                <Bot size={14} /> AI DIALPLAN EXPLANATION
              </div>
              <div className="au-mono" style={{ fontSize: 13, color: "var(--au-text-2)", lineHeight: 1.8, whiteSpace: "pre-wrap", fontFamily: "var(--au-font-mono)" }}>{explanation}</div>
            </div>
          )}
        </div>

        <aside
          className="au-glass"
          style={{ padding: 16, fontSize: 12 }}
        >
          <h3 style={{ fontSize: 13, margin: "0 0 10px 0", color: "var(--au-accent-ink)", letterSpacing: 1, textTransform: "uppercase" }}>
            Snippets — {info.label}
          </h3>
          <div style={{ color: "var(--au-muted)", marginBottom: 10, fontSize: 11, lineHeight: 1.5 }}>
            Type word + <kbd style={{ background: "var(--au-glass-strong)", border: "1px solid var(--au-hair)", padding: "2px 6px", borderRadius: 4, fontFamily: "var(--au-font-mono)" }}>Tab</kbd>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 320, overflowY: "auto" }}>
            {Object.keys(SNIPPETS[mode] || {}).map((k) => (
              <div key={k} style={{ padding: "5px 10px", background: "var(--au-glass)", border: "1px solid var(--au-hair)", borderRadius: 4, fontFamily: "var(--au-font-mono)", fontSize: 12 }}>
                <span style={{ color: "var(--au-accent-ink)", fontWeight: 700 }}>{k}</span> <span style={{ color: "var(--au-muted)" }}>+ Tab</span>
              </div>
            ))}
          </div>
        </aside>
      </div>
    </div>
  );
}
